import type { CommodityInfo } from "@/interfaces/CommodityInfo";
import type { Option } from "@/interfaces/Option";
import { AppSettingsService } from "@/service/AppSettingsService";
import Logger from "../utils/Logger";
import { apiGet } from "./api";

interface CommodityResponse {
	responseObject: CommodityInfo[];
}

const fetchCommodity = async (): Promise<CommodityInfo[]> => {
	// Development-only mock; never used in production — let real errors surface.
	const mockResult: CommodityInfo[] = [
		{ id: 1, name: "Avocado" },
		{ id: 2, name: "Soy" },
		{ id: 3, name: "Timber" },
		{ id: 4, name: "Sugar" },
		{ id: 5, name: "Coconut" },
		{ id: 6, name: "Milk" },
		{ id: 7, name: "Rice" },
	];

	try {
		Logger.info("Fetching commodity data from API", ["CommodityApi"]);
		sessionStorage.removeItem("commodityArrayListStored");

		const commodityResult = await apiGet<CommodityResponse>("/lookup?type=commodities");

		const result: CommodityInfo[] = commodityResult.responseObject;
		if (result) {
			Logger.info(`Successfully fetched ${result.length} commodities`, ["CommodityApi"]);
			return result;
		}

		Logger.warn("CommodityApi Response without response object", ["CommodityApi"]);
		return [];
	} catch (err) {
		Logger.error("Error fetching commodity data from remote API", err, ["CommodityApi"]);

		// Use mock only in development; in production re-throw to surface real errors.
		if (AppSettingsService.isDevelopmentMode()) {
			Logger.warn("Development mode: falling back to mock commodity list", ["CommodityApi"]);
			return mockResult;
		}

		Logger.error("Commodity fetch failed — not using mock fallback, re-throwing", null, [
			"CommodityApi",
		]);
		throw err;
	}
};

const commodityFromRemote = async (): Promise<Option[]> => {
	const response = await fetchCommodity();
	Logger.debug("Processing commodity response", ["CommodityApi"]);

	if (!Array.isArray(response)) {
		Logger.error("Expected responseObject to be an array, but received:", response, [
			"CommodityApi",
		]);
		return []; // Return an empty array to prevent runtime errors
	}

	const commodityArrayList: Option[] = response
		.map((item: CommodityInfo) => ({
			label: item.name?.toUpperCase() || "", // Use empty string as a fallback
			value: item.name || "",
		}))
		.filter((item) => item.label && item.value) // Filter out invalid items
		.sort((a, b) => a.value.localeCompare(b.value)); // Use localeCompare for simpler string sorting;

	Logger.info(`Processed ${commodityArrayList.length} valid commodity options`, ["CommodityApi"]);

	if (commodityArrayList.length > 2) {
		sessionStorage.setItem("commodityArrayListStored", JSON.stringify(commodityArrayList));
		Logger.debug("Cached commodity list in session storage", ["CommodityApi"]);
	}

	return commodityArrayList;
};

const getCommodityList = async () => {
	const storedCommodityArrayList = sessionStorage.getItem("commodityArrayListStored");

	if (storedCommodityArrayList) {
		Logger.debug("Loading commodity list from cache", ["CommodityApi"]);
		return JSON.parse(storedCommodityArrayList) as Option[];
	}
	Logger.debug("No cached commodity list found, fetching from remote", ["CommodityApi"]);
	return await commodityFromRemote();
};

export const CommodityApi = {
	getCommodityList,
};
